import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Clock, Lock, ArrowLeft, Home } from 'lucide-react';

/**
 * A friendly overlay component that shows when a feature is not available
 * Can be used for "Coming Soon" or "Disabled" states
 */
const ModuleAccessOverlay = ({ 
  status = 'coming_soon', // 'coming_soon' | 'disabled'
  featureName = 'This feature',
  description = null,
  showBackButton = true,
  showHomeButton = true,
  customMessage = null
}) => {
  const navigate = useNavigate();

  const isComingSoon = status === 'coming_soon';
  const isDisabled = status === 'disabled';

  const defaultDescriptions = {
    coming_soon: `${featureName} is coming soon! We're working hard to bring you this feature. Please check back later.`,
    disabled: `${featureName} is currently not available for your account. Please contact support if you believe this is an error.`
  };

  const displayDescription = customMessage || description || defaultDescriptions[status] || defaultDescriptions.coming_soon;

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-4">
      <Card className="max-w-md w-full border-2 border-dashed border-gray-300 bg-gray-50/50">
        <CardContent className="p-8 text-center">
          {/* Icon */}
          <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 ${
            isComingSoon ? 'bg-yellow-100' : 'bg-gray-200'
          }`}>
            {isComingSoon ? (
              <Clock className="w-10 h-10 text-yellow-600" />
            ) : (
              <Lock className="w-10 h-10 text-gray-500" />
            )}
          </div>

          {/* Title */}
          <h2 className={`text-2xl font-bold mb-3 ${
            isComingSoon ? 'text-yellow-800' : 'text-gray-700'
          }`}>
            {isComingSoon ? 'Coming Soon!' : 'Feature Not Available'}
          </h2>

          {/* Description */}
          <p className="text-gray-600 mb-6 leading-relaxed">
            {displayDescription}
          </p>

          {/* Feature name badge */}
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 ${
            isComingSoon ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-200 text-gray-700'
          }`}>
            {isComingSoon ? (
              <Clock className="w-4 h-4" />
            ) : (
              <Lock className="w-4 h-4" />
            )}
            <span className="font-medium text-sm">{featureName}</span>
          </div>

          {/* Action buttons */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {showBackButton && (
              <Button
                variant="outline"
                onClick={() => navigate(-1)}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Go Back
              </Button>
            )}
            {showHomeButton && (
              <Button
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-2 bg-[#0A7EA4] hover:bg-[#0A7EA4]/90"
              >
                <Home className="w-4 h-4" />
                Go to Dashboard
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ModuleAccessOverlay;
